import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getTermRead, termReadErrorMessage } from "../../../../api/termRead";
import { useActiveCompanyId } from "../../../../api/useActiveCompanyId";
import { useActiveCompanyIdApi } from "../../../../api/useActiveCompanyIdApi";
import { formatCurrency } from "../../../shared/format";
import "./recordsPreview.scss";

type RecordServiceItem = {
    id: number;
    title?: string | null;
};

type RecordItem = {
    id: number;
    dateTime: string | null;
    master?: {
        id: number;
        name?: string | null;
    } | null;
    client?: {
        id: number;
        name?: string | null;
        phone?: string | null;
    } | null;
    services?: RecordServiceItem[];
    amount: number;
    amountFrom?: number | null;
    amountTo?: number | null;
    confirmed?: boolean;
    online?: boolean;
};

type RecordsPreviewResponse = {
    items?: RecordItem[];
};

function formatDateParts(value: string | null | undefined): { date: string; time: string } {
    if (!value) {
        return { date: "—", time: "" };
    }

    const normalized = value.includes("T") ? value : value.replace(" ", "T");
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) {
        return { date: value, time: "" };
    }

    return {
        date: new Intl.DateTimeFormat("ru-RU", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        }).format(date),
        time: new Intl.DateTimeFormat("ru-RU", {
            hour: "2-digit",
            minute: "2-digit",
        }).format(date),
    };
}

function servicesLabel(services?: RecordServiceItem[]): string {
    if (!services || services.length === 0) {
        return "Услуги не указаны";
    }

    return services
        .map((service) => service.title?.trim())
        .filter(Boolean)
        .join(", ");
}

const rubNumberFormatter = new Intl.NumberFormat("ru-RU", {
    maximumFractionDigits: 0,
});

function formatRecordAmount(item: Pick<RecordItem, "amount" | "amountFrom" | "amountTo">): string {
    const amountFrom = Number(item.amountFrom ?? NaN);
    const amountTo = Number(item.amountTo ?? NaN);

    if (Number.isFinite(amountFrom) && Number.isFinite(amountTo) && amountTo > amountFrom) {
        return `${rubNumberFormatter.format(amountFrom)} - ${rubNumberFormatter.format(amountTo)} ₽`;
    }

    return formatCurrency(item.amount);
}

export const RecordsPreview: React.FC = () => {
    const { value: activeCompanyId, resolved: activeCompanyResolved } = useActiveCompanyId();
    const { value: activeCompanyIdApi, resolved: activeCompanyApiResolved } = useActiveCompanyIdApi();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [emptyState, setEmptyState] = useState<string | null>(null);
    const [forbidden, setForbidden] = useState(false);
    const [items, setItems] = useState<RecordItem[]>([]);
    const [retryRequest, setRetryRequest] = useState(0);

    useEffect(() => {
        let alive = true;
        const controller = new AbortController();

        if (!activeCompanyResolved || !activeCompanyApiResolved) {
            setLoading(true);
            setError(null);
            setEmptyState(null);
            setForbidden(false);
            setItems([]);

            return () => {
                alive = false;
                controller.abort();
            };
        }

        if (!activeCompanyId) {
            setLoading(false);
            setError(null);
            setEmptyState("Нет доступных точек для работы с записями.");
            setForbidden(false);
            setItems([]);

            return () => {
                alive = false;
                controller.abort();
            };
        }

        if (!activeCompanyIdApi) {
            setLoading(false);
            setError(null);
            setEmptyState("Для выбранной точки записи пока не подключены.");
            setForbidden(false);
            setItems([]);

            return () => {
                alive = false;
                controller.abort();
            };
        }

        const loadRecords = async () => {
            try {
                setLoading(true);
                setError(null);
                setEmptyState(null);
                setForbidden(false);

                const response = await getTermRead<RecordsPreviewResponse>("/lkfr/records/future", { limit: 2 }, controller.signal);
                if (!alive) {
                    return;
                }

                setItems(response?.items ?? []);
            } catch (requestError: any) {
                if (!alive) {
                    return;
                }

                if (Number(requestError?.status) === 403) {
                    setForbidden(true);
                    setItems([]);
                    return;
                }

                setError(termReadErrorMessage(requestError, "Не удалось загрузить записи"));
            } finally {
                if (alive) {
                    setLoading(false);
                }
            }
        };

        void loadRecords();

        return () => {
            alive = false;
            controller.abort();
        };
    }, [activeCompanyApiResolved, activeCompanyId, activeCompanyIdApi, activeCompanyResolved, retryRequest]);

    return (
        <section className="records-preview-block" id="records-preview">
            <div className="records-preview-head">
                <h2 className="section-title">Ближайшие записи</h2>
                <Link to="/records" className="records-preview-all">
                    Все
                </Link>
            </div>

            {loading ? (
                <div className="card records-preview-card records-preview-state">
                    Загружаем записи...
                </div>
            ) : null}

            {!loading && forbidden ? (
                <div className="card records-preview-card records-preview-state">
                    Нет доступа к просмотру записей по текущей точке.
                </div>
            ) : null}

            {!loading && !forbidden && emptyState ? (
                <div className="card records-preview-card records-preview-state">
                    {emptyState}
                </div>
            ) : null}

            {!loading && !forbidden && !emptyState && error ? (
                <div className="card records-preview-card records-preview-state error">
                    {error}
                    <button type="button" onClick={() => setRetryRequest((value) => value + 1)}>Повторить</button>
                </div>
            ) : null}

            {!loading && !forbidden && !emptyState && !error && items.length === 0 ? (
                <div className="card records-preview-card records-preview-state">
                    На ближайшее время записей нет.
                </div>
            ) : null}

            {!loading && !forbidden && !emptyState && !error ? (
                <div className="records-preview-list">
                    {items.map((item) => {
                        const parts = formatDateParts(item.dateTime);

                        return (
                            <Link to="/records" className="records-preview-link" key={item.id}>
                                <div className="records-preview-subheader">
                                    <span>{parts.date}</span>
                                    <span>{parts.time}</span>
                                </div>

                                <div className="card records-preview-card">
                                    <div className="records-preview-row">
                                        <div>
                                            <div className="records-preview-client">
                                                {item.client?.name || "Клиент не указан"}
                                            </div>
                                            <div className="records-preview-services">
                                                {servicesLabel(item.services)}
                                            </div>
                                            <div className="records-preview-meta">
                                                Мастер: {item.master?.name || "Не назначен"}
                                            </div>
                                        </div>

                                        <div className="records-preview-side">
                                            <div className="records-preview-amount">
                                                {formatRecordAmount(item)}
                                            </div>
                                            <div className="records-preview-badges">
                                                {item.online ? <span className="records-preview-badge">Онлайн</span> : null}
                                                {item.confirmed ? <span className="records-preview-badge confirmed">Ок</span> : null}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            ) : null}
        </section>
    );
};

export default RecordsPreview;
